import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { UserService } from '../../core/services/data/user.service';
import { IEvent, IMemberRole, IUser } from '../../types/datatypes';
import { UtilitiesService } from '../../core/services/utilities.service';

@Component({
	selector: 'app-event-ticket',
	templateUrl: './event-ticket.component.html',
	styleUrls: ['./event-ticket.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EventTicketComponent implements OnInit, OnDestroy {
	// eslint-disable-next-line @angular-eslint/no-input-rename
	@Input({ required: true }) event: IEvent;

	public author: IUser = null;

	public loading = false;

	public calendarIcon = faCalendarAlt;

	private author$: Subscription;

	get discountText(): string {
		let role = 'Your';
		if (this.userService.isGoldMember()) {
			role = 'Gold';
		}
		if (this.userService.isPlatinumMember()) {
			role = 'Platinum';
		}
		return `${role} Registration Cost`;
	}

	get discountAmount(): number {
		const regularPrice = this.event.tickets.standardPrice;
		return regularPrice - this.eventPrice;
	}

	get standardPrice(): number {
		return this.event.tickets.standardPrice;
	}

	get eventPrice(): number {
		if (!this.author) {
			return this.getPricePerMembershipLevel('guest');
		}
		const memberLevel = this.userService.getMemberLevelName((this.author.memberRole as IMemberRole)?.roleAccess ?? 0);
		return this.getPricePerMembershipLevel(memberLevel);
	}

	get freeMemberPricing(): number {
		return this.event.tickets.prices.find((price) => price.membershipLevel === 'free')?.price ?? this.event.tickets.standardPrice;
	}

	get goldPricing(): number {
		return this.event.tickets.prices.find((price) => price.membershipLevel === 'gold')?.price ?? this.event.tickets.standardPrice;
	}

	get goldDiscountPercent(): number {
		const discount = this.standardPrice - this.goldPricing;
		const percent = (discount / this.standardPrice) * 100;
		return percent <= 0.5 ? 1 : Math.round(percent);
	}

	get platinumPricing(): number {
		return this.event.tickets.prices.find((price) => price.membershipLevel === 'platinum')?.price ?? this.event.tickets.standardPrice;
	}

	get platinumDiscountPercent(): number {
		const discount = this.standardPrice - this.platinumPricing;
		const percent = (discount / this.standardPrice) * 100;
		return percent <= 0.5 ? 1 : Math.round(percent);
	}

	get hasAnyDiscount() {
		const prices = [this.freeMemberPricing, this.goldPricing, this.platinumPricing];
		return prices.some((pricing) => pricing !== this.standardPrice);
	}

	constructor(
		public userService: UserService,
		public utilitiesService: UtilitiesService,
	) { }

	ngOnInit() {
		if (!this.userService.loggedIn) {
			this.loading = false;
			this.utilitiesService.saveRoute(window.location.pathname);

			return;
		}
		this.loading = true;
		this.author$ = this.userService.author$.subscribe((author: any) => {
			this.author = author;
			this.loading = false;
		});
	}

	ngOnDestroy() {
		this.author$?.unsubscribe();
	}

	getFormattedDate() {
		const date = moment.tz(this.event.time.start, 'America/New_York').format('MMM D, YYYY h:mm A z');
		return date;
	}

	getPricePerMembershipLevel(level: 'guest' | 'free' | 'gold' | 'platinum') {
		const pricePerLevel = this.event.tickets.prices.find((price) => price.membershipLevel === level);
		if (!pricePerLevel) {
			return this.event.tickets.standardPrice;
		}
		return pricePerLevel.price;
	}

	showDiscountLine() {
		return this.discountAmount !== 0;
	}
}
